// src/pages/RegistrationWizardPage.tsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getWizardPathById } from "../wizard/wizardPaths";
import WizardTopBar from "../wizard/WizardTopBar";
import WizardShell from "../wizard/WizardShell";
import type { WizardStepId } from "../wizard/wizardTypes";
import { createDefaultWizardFormState, type WizardFormState } from "../wizard/wizardState";
import ParentInfoStep from "../steps/ParentInfoStep";
import VolunteerInfoStep from "../steps/VolunteerInfoStep";
import ChildStep from "../steps/ChildStep";
import EmergencyContactsStep from "../steps/EmergencyContactsStep";
import ReviewSubmitStep from "../steps/ReviewSubmitStep";
import { submitRegistration } from "../services/registrationService";
import {
  validateParentInfo,
  validateVolunteers,
  validateChildren,
  validateEmergencyContacts,
} from "../utils/validators";

function useQueryParam(name: string) {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search).get(name), [location.search, name]);
}

export default function RegistrationWizardPage() {
  const navigate = useNavigate();
  const pathParam = useQueryParam("path");
  const pathDef = useMemo(() => getWizardPathById(pathParam), [pathParam]);

  const [formState, setFormState] = useState<WizardFormState>(() => createDefaultWizardFormState());
  const [stepIndex, setStepIndex] = useState(0);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = pathDef.steps;
  const activeStep = steps[stepIndex];
  const topBarActiveIndex = stepIndex;

  function goBack() {
    if (stepIndex === 0) {
      navigate("/");
      return;
    }
    setStepErrors({});
    setStepIndex((v) => Math.max(0, v - 1));
  }

  function validateStepById(stepId: WizardStepId) {
    if (stepId === "parentInfo") return validateParentInfo(formState.parentInfo);

    if (stepId === "volunteerInfo") {
        const requirePassword = pathDef.id === "volunteerAndChild";
        const requirePickupType = pathDef.id !== "volunteerOnly";
        return validateVolunteers(
        formState.volunteers,
        requirePassword,
        requirePickupType
        );
    }

    if (stepId === "children") return validateChildren(formState.children);
    if (stepId === "emergency")
        return validateEmergencyContacts(formState.emergencyContacts);

    return { isValid: true, errors: {} };
    }

    function canNavigateToStep(targetIndex: number) {
    // Always allow going backward or staying put
    if (targetIndex <= stepIndex) return true;

    // To go forward, every previous step must be valid
    for (let i = 0; i < targetIndex; i += 1) {
        const prevStepId = steps[i].id;
        const result = validateStepById(prevStepId);
        if (!result.isValid) return false;
    }

    return true;
    }


  function validateCurrentStep() {
    const stepId = activeStep.id;
    if (stepId === "parentInfo") {
      return validateParentInfo(formState.parentInfo);
    }
    if (stepId === "volunteerInfo") {
      const requirePassword = pathDef.id === "volunteerAndChild";
      const requirePickupType = pathDef.id !== "volunteerOnly";
      return validateVolunteers(formState.volunteers, requirePassword, requirePickupType);
    }
    if (stepId === "children") {
      return validateChildren(formState.children);
    }
    if (stepId === "emergency") {
      return validateEmergencyContacts(formState.emergencyContacts);
    }
    return { isValid: true, errors: {} };
  }

  function goNext() {
    const validation = validateCurrentStep();
    setStepErrors(validation.errors);
    if (!validation.isValid) return;

    if (activeStep.id === "review") {
      // ReviewSubmitStep handles submission button, but keep safety here.
      return;
    }

    setStepErrors({});
    setStepIndex((v) => Math.min(steps.length - 1, v + 1));
  }

  function renderStep(stepId: WizardStepId) {
    if (stepId === "parentInfo") {
      return (
        <ParentInfoStep
          value={formState.parentInfo}
          onChange={(next) => setFormState((s) => ({ ...s, parentInfo: next }))}
          errors={stepErrors}
        />
      );
    }

    if (stepId === "volunteerInfo") {
      return (
        <VolunteerInfoStep
          volunteers={formState.volunteers}
          onChange={(next) => setFormState((s) => ({ ...s, volunteers: next }))}
          errors={stepErrors}
          pathId={pathDef.id}
        />
      );
    }

    if (stepId === "children") {
      return (
        <ChildStep
          childrenValue={formState.children}
          onChange={(next) => setFormState((s) => ({ ...s, children: next }))}
          errors={stepErrors}
        />
      );
    }

    if (stepId === "emergency") {
      return (
        <EmergencyContactsStep
          value={formState.emergencyContacts}
          onChange={(next) => setFormState((s) => ({ ...s, emergencyContacts: next }))}
          errors={stepErrors}
        />
      );
    }

    return (
      <ReviewSubmitStep
        pathTitle={pathDef.landingTitle}
        formState={formState}
        onEditStep={(targetIndex) => {
          setStepErrors({});
          setStepIndex(targetIndex);
        }}
        onSubmit={async () => {
          setSubmitError(null);
          setIsSubmitting(true);

          // Validate all steps before submitting
          const results = steps.map((s) => {
            const id = s.id;
            if (id === "parentInfo") return validateParentInfo(formState.parentInfo);
            if (id === "volunteerInfo") {
              const requirePassword = pathDef.id === "volunteerAndChild";
              const requirePickupType = pathDef.id !== "volunteerOnly";
              return validateVolunteers(formState.volunteers, requirePassword, requirePickupType);
            }
            if (id === "children") return validateChildren(formState.children);
            if (id === "emergency") return validateEmergencyContacts(formState.emergencyContacts);
            return { isValid: true, errors: {} };
          });

          const firstInvalidIndex = results.findIndex((r) => !r.isValid);
          if (firstInvalidIndex >= 0) {
            setStepErrors(results[firstInvalidIndex].errors);
            setStepIndex(firstInvalidIndex);
            setIsSubmitting(false);
            return;
          }

          try {
            const registrationResponse = await submitRegistration(formState, pathDef.id);

            if (!registrationResponse.success) {
              setSubmitError(registrationResponse.error || "Registration failed");
              setIsSubmitting(false);
              return;
            }

            // success
            navigate("/");
          } catch (err: any) {
            setSubmitError(err?.message || "Network error");
            setIsSubmitting(false);
          }
        }}
        submitError={submitError}
        submitting={isSubmitting}
      />
    );
  }

  const isLast = activeStep.id === "review";
  const primaryLabel = isLast ? "Submit" : "Next";

  // For review, disable Next and let ReviewSubmitStep own submission button.
  const canGoNext = !isLast;

  return (
    <div className="space-y-4">
      <WizardTopBar
        variant={pathDef.topBarVariant}
        activeIndex={topBarActiveIndex}
        total={steps.length}
        isStepClickable={(idx) => canNavigateToStep(idx)}
        onStepClick={(idx) => {
            if (!canNavigateToStep(idx)) {
            // Optional: jump user to the first invalid step and show its errors
            for (let i = 0; i < idx; i += 1) {
                const check = validateStepById(steps[i].id);
                if (!check.isValid) {
                setStepErrors(check.errors);
                setStepIndex(i);
                return;
                }
            }
            return;
            }

            setStepErrors({});
            setStepIndex(idx);
        }}
        />



      <WizardShell
        title={activeStep.title}
        subtitle={pathDef.landingTitle}
        canGoBack={true}
        canGoNext={canGoNext}
        primaryLabel={primaryLabel}
        onBack={goBack}
        onNext={goNext}
        footerHint="Use Next to continue. Fix any highlighted fields."
      >
        {renderStep(activeStep.id)}
      </WizardShell>
    </div>
  );
}
